import React from 'react';
import { DatePicker, Form, Input, InputNumber, Switch } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';

/** The form's values: the API's ISO expiry is a Dayjs while it is being edited. */
export interface ReferralCodeFormValues {
  code: string;
  freeDays: number;
  isActive?: boolean;
  maxRedemptions?: number | null;
  expiresAt?: Dayjs | null;
  note?: string | null;
}

/** API record → form. */
export const toFormValues = (record?: Record<string, any>) =>
  record ? { ...record, expiresAt: record.expiresAt ? dayjs(record.expiresAt) : null } : undefined;

/**
 * Form → API. Empty optional fields are sent as `null`, not left out, so
 * clearing a limit or an expiry on an existing code actually removes it.
 */
export const toPayload = (values: ReferralCodeFormValues) => ({
  code: values.code?.trim().toUpperCase(),
  freeDays: values.freeDays,
  isActive: values.isActive ?? true,
  maxRedemptions: values.maxRedemptions ?? null,
  expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
  note: values.note?.trim() || null,
});

/**
 * Fields shared by the create and edit pages. [codeLocked] is set once members
 * have redeemed the code: the API refuses to rename it from then on.
 */
export const ReferralCodeFormFields: React.FC<{ codeLocked?: boolean }> = ({ codeLocked }) => (
  <>
    <Form.Item
      label="Code"
      name="code"
      extra={
        codeLocked
          ? 'Members have already redeemed this code, so its text can no longer change.'
          : 'What people type on the sign-up paywall. 4–24 letters, digits or hyphens; not case-sensitive.'
      }
      rules={[
        { required: true, message: 'Enter a code' },
        {
          pattern: /^[A-Za-z0-9][A-Za-z0-9-]{3,23}$/,
          message: '4–24 letters, digits or hyphens',
        },
      ]}
      normalize={(value?: string) => value?.toUpperCase()}
    >
      <Input placeholder="SUMMER30" disabled={codeLocked} style={{ maxWidth: 320 }} />
    </Form.Item>
    <Form.Item
      label="Free Days"
      name="freeDays"
      extra="Premium days given to a member who enters this code."
      rules={[{ required: true, message: 'Enter the number of free days' }]}
    >
      <InputNumber min={1} max={3650} precision={0} addonAfter="days" style={{ width: 200 }} />
    </Form.Item>
    <Form.Item label="Active" name="isActive" valuePropName="checked" initialValue={true}>
      <Switch />
    </Form.Item>
    <Form.Item
      label="Max Redemptions"
      name="maxRedemptions"
      extra="How many members may use it in total. Leave empty for no limit."
    >
      <InputNumber min={1} precision={0} style={{ width: 200 }} />
    </Form.Item>
    <Form.Item
      label="Expires"
      name="expiresAt"
      extra="The code stops working after this time. Leave empty for no expiry."
    >
      <DatePicker showTime style={{ width: 240 }} />
    </Form.Item>
    <Form.Item label="Note" name="note" extra="What the code is for. Only shown here.">
      <Input.TextArea rows={2} maxLength={500} />
    </Form.Item>
  </>
);
